import { 
  getMeetings, 
  getMeeting, 
  createMeeting as createMeetingFirestore, 
  deleteMeeting as deleteMeetingFirestore,
  getAllMeetings as getAllMeetingsFirestore
} from '../utils/firestoreUtils';
import { auth } from './firebase/config';
import { getCurrentUserId, fixAzureUserId } from '../utils/authUtils';
import { doc, updateDoc, serverTimestamp, collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from './firebase/config';

// Délai simulé pour imiter les appels réseau
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Mode développement (pour simuler des données sans appeler l'API)
const DEV_MODE = false;

// Fonction utilitaire pour gérer les erreurs d'authentification
const handleAuthError = async (error, operation, retryFn) => {

  // Vérifier si c'est une erreur d'authentification
  if ((error.code && (error.code === 'permission-denied' || error.code === 'unauthenticated')) || 
      (error.message && (error.message.includes('permission-denied') || 
                         error.message.includes('unauthenticated') || 
                         error.message.includes('Utilisateur non authentifié')))) {

    // Tenter de récupérer le contexte d'authentification
    if (typeof window !== 'undefined' && window.__authContext) {
      try {
        const refreshFn = window.__authContext.refreshFirebaseToken;
        if (typeof refreshFn === 'function') {
          const refreshed = await refreshFn();
          
          if (refreshed) {
            if (typeof retryFn === 'function') {
              // Réessayer l'opération originale
              return await retryFn();
            }
          } else {

            // Stocker un flag pour informer l'application du problème
            localStorage.setItem('auth_refresh_failed', 'true');
          }
        } else {

        }
      } catch (refreshError) {

      }
    } else {

      // Flag pour indiquer un besoin de reconnexion
      localStorage.setItem('auth_refresh_needed', 'true');
    }
  }
  
  // Si le rafraîchissement a échoué ou ce n'était pas une erreur d'authentification
  throw error;
};

// Données mockées pour le mode développement
const MOCK_MEETINGS = [
  {
    id: 'mock-meeting-1',
    title: 'Réunion de développement',
    description: 'Discussion sur les nouvelles fonctionnalités',
    date: '2023-04-15',
    createdAt: new Date('2023-04-10'),
    duration: 60,
    participants: ['John Doe', 'Jane Smith'],
    status: 'completed'
  },
  {
    id: 'mock-meeting-2',
    title: 'Planification sprint',
    description: 'Planification du prochain sprint',
    date: '2023-04-20',
    createdAt: new Date('2023-04-15'),
    duration: 90,
    participants: ['John Doe', 'Jane Smith', 'Robert Johnson'],
    status: 'pending'
  }
];

// Cache pour minimiser les appels répétés
const cache = {
  meetings: null,
  meetingsTimestamp: 0,
  CACHE_DURATION: 60000 // 1 minute en ms
};

// Service pour les réunions
const meetingService = {
  // Récupérer toutes les réunions
  getAllMeetings: async () => {
    const retryOperation = async () => meetingService.getAllMeetings();
    
    try {
      // Force le nettoyage du cache pour le débogage
      cache.meetings = null;
      
      // En mode développement, retourner les données mockées
      if (DEV_MODE) {
        await delay(500); // Simuler un délai réseau
        return MOCK_MEETINGS;
      }
      
      // Utiliser la nouvelle fonction getAllMeetings sans filtrage par userId
      try {
        const allMeetings = await getAllMeetingsFirestore();
        return allMeetings;
      } catch (fetchError) {
        // Essayer de gérer l'erreur d'authentification avant de revenir aux mocks
        return await handleAuthError(fetchError, 'récupération de toutes les réunions', retryOperation);
      }
    } catch (error) {

      return MOCK_MEETINGS;
    }
  },
  
  // Récupérer une réunion par ID
  getMeetingById: async (id) => {
    const retryOperation = async () => meetingService.getMeetingById(id);
    
    try {
      // En mode développement, retourner les données mockées
      if (DEV_MODE) {
        await delay(300); // Simuler un délai réseau
        const meeting = MOCK_MEETINGS.find(m => m.id === id);
        return meeting || null;
      }

      return await getMeeting(id);
    } catch (error) {
      try {
        // Essayer de gérer l'erreur d'authentification
        return await handleAuthError(error, `récupération de la réunion ${id}`, retryOperation);
      } catch (finalError) {

        const meeting = MOCK_MEETINGS.find(m => m.id === id);
        return meeting || null;
      }
    }
  },
  
  // Créer une nouvelle réunion
  createMeeting: async (meetingData) => {
    try {
      // Obtenir l'ID utilisateur à l'aide de notre utilitaire
      const userId = getCurrentUserId();
      
      if (!userId) {
        throw new Error('Utilisateur non authentifié');
      }
      
      // Ajouter le préfixe 'azure-' si nécessaire
      const normalizedUserId = fixAzureUserId(userId);
      
      // Créer l'objet réunion avec l'ID utilisateur approprié
      const newMeeting = {
        ...meetingData,
        userId: normalizedUserId,
        status: 'pending',
        transcriptionId: null
      };

      // Créer la réunion dans Firestore
      const meetingId = await createMeetingFirestore(newMeeting);
      
      // Invalider le cache
      cache.meetings = null;
      
      return { ...newMeeting, id: meetingId };
    } catch (error) {

      throw error;
    }
  },
  
  // Mettre à jour une réunion
  updateMeeting: async (id, meetingData) => {
    try {
      
      // Référence au document de la réunion
      const meetingRef = doc(db, 'meetings', id);
      
      // Préparer les données avec timestamp de mise à jour
      const updatedData = {
        ...meetingData,
        updatedAt: serverTimestamp()
      };

      // Mettre à jour le document dans Firestore
      await updateDoc(meetingRef, updatedData);
      
      // Invalider le cache
      cache.meetings = null;

      return { id, ...meetingData, updatedAt: new Date().toISOString() };
    } catch (error) {

      throw error;
    }
  },
  
  // Supprimer une réunion
  deleteMeeting: async (id) => {
    try {
      await deleteMeetingFirestore(id);
      
      // Invalider le cache
      cache.meetings = null;
      
      return { success: true, id };
    } catch (error) {

      throw error;
    }
  },

  /**
   * Écoute en temps réel toutes les réunions accessibles à l'utilisateur
   * @param {function} callback - Fonction appelée avec les réunions mises à jour
   * @param {object} options - Options pour l'écoute
   * @returns {function} - Fonction de désabonnement
   */
  onAllMeetings: (callback, options = {}) => {
    try {
      const meetingsRef = collection(db, 'meetings');
      const q = query(meetingsRef, orderBy('createdAt', 'desc'));
      
      return onSnapshot(q, (querySnapshot) => {
        const meetings = [];
        querySnapshot.forEach(doc => {
          const data = doc.data();
          meetings.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          });
        });
        
        callback(meetings);
      }, (error) => {

        callback([]);
      });
    } catch (error) {

      callback([]);
      return () => {}; // Retourner une fonction vide en cas d'erreur
    }
  },

  /**
   * Écoute en temps réel les réunions d'un utilisateur spécifique
   * @param {string} userId - ID de l'utilisateur
   * @param {function} callback - Fonction appelée avec les réunions mises à jour
   * @returns {function} - Fonction de désabonnement
   */
  onUserMeetings: (userId, callback) => {
    try {
      const meetingsRef = collection(db, 'meetings');
      const q = query(
        meetingsRef, 
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      return onSnapshot(q, (querySnapshot) => {
        const meetings = [];
        querySnapshot.forEach(doc => {
          const data = doc.data();
          meetings.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          });
        });
        
        callback(meetings);
      }, (error) => {

        callback([]);
      });
    } catch (error) {

      callback([]);
      return () => {}; // Retourner une fonction vide en cas d'erreur
    }
  },

  /**
   * Écoute en temps réel les réunions partagées avec un utilisateur spécifique
   * @param {string} userId - ID de l'utilisateur
   * @param {function} callback - Fonction appelée avec les réunions mises à jour
   * @returns {function} - Fonction de désabonnement
   */
  onSharedMeetings: (userId, callback) => {
    try {
      const meetingsRef = collection(db, 'meetings');
      const q = query(
        meetingsRef, 
        where('sharedWith', 'array-contains', userId),
        orderBy('createdAt', 'desc')
      );
      
      return onSnapshot(q, (querySnapshot) => {
        const meetings = [];
        querySnapshot.forEach(doc => {
          const data = doc.data();
          meetings.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          });
        });
        
        callback(meetings);
      }, (error) => {

        callback([]);
      });
    } catch (error) {

      callback([]);
      return () => {}; // Retourner une fonction vide en cas d'erreur
    }
  }
};

export default meetingService; 